import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { OuterNav } from "../layouts/PublicNav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Star, StarHalf, Lock, ChartLineUp, ArrowLeft, MagnifyingGlass } from "@phosphor-icons/react";
import SeoPageHead from "../components/SeoPageHead";
import useSiteContent from "../lib/useSiteContent";
import { formatDate } from "../lib/date";

// Star-meaning legend (5 -> 1). Shared between the public vote panel (so a
// patron knows exactly what each star represents before rating) and the
// admin "sentiment_label" bucketing on the backend (`_sentiment_label` in
// sentiment.py) — keep these two in sync if the wording ever changes.
export const SENTIMENT_SCALE = [
  { stars: 5, label: "Very Positive" },
  { stars: 4, label: "Positive" },
  { stars: 3, label: "Neutral" },
  { stars: 2, label: "Negative" },
  { stars: 1, label: "Very Negative" },
];

// ============================================================================
// /sentiment — public landing
// ============================================================================
export function SentimentList() {
  const loc = useLocation();
  const nav = useNavigate();
  const c = useSiteContent();
  const T = (k, fallback) => (c[k] !== undefined ? c[k] : fallback);
  // ITER 135 — admin-controlled: show the Very Positive/…/Very Negative
  // label chip next to ratings on public cards (both logged-in and
  // logged-out visitors). Defaults ON; admin's own console always shows
  // the label regardless of this toggle (see AdminSentiment in Admin.jsx).
  const showLabels = c.sentiment_public_labels_enabled !== "false";
  const inPanel = loc.pathname.startsWith("/panelist");
  const base = inPanel ? "/panelist/sentiment" : "/sentiment";
  const [enabled, setEnabled] = useState(() => {
    const cached = api.getCached("/sentiment/feature");
    return cached ? !!cached.public_sentiment_enabled : true;
  });
  const [categories, setCategories] = useState(() => api.getCached("/sentiment/categories") || []);
  const [items, setItems] = useState(() => api.getCached("/sentiment/items") || []);
  const [loading, setLoading] = useState(() => !api.getCached("/sentiment/items"));
  const [active, setActive] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/sentiment/feature").then((r) => setEnabled(r.data.public_sentiment_enabled)).catch(() => {});
    api.get("/sentiment/categories").then((r) => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    const params = {};
    if (active) params.category = active;
    if (search.trim()) params.q = search.trim();
    setLoading(true);
    api.get("/sentiment/items", { params }).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [active, search]);

  if (!enabled) {
    return (
      <div className="min-h-screen bg-white themable-public">
        <OuterNav />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-neutral-900">Public Sentiment is currently offline</h1>
          <p className="text-neutral-500 mt-3">Check back soon — our team is preparing the next round of voting.</p>
          <Button onClick={() => nav("/")} className="rounded-full mt-6 bg-mango hover:bg-mango/90">Back home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white themable-public">
      <SeoPageHead
        pageKey="sentiment"
        path="/sentiment"
        defaultTitle="Public Sentiment — How Pakistanis Rate Everything | oodel."
        defaultDescription="Browse, rate and discuss what Pakistanis really think about brands, services and policy."
      />
      <OuterNav />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="pb-5 mb-8 md:flex md:items-end md:justify-between gap-6">
          <div>
            <div className="overline mb-2 text-mango">PUBLIC SENTIMENT — POWERED BY YOU</div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900" data-testid="sentiment-hero-title">{T("sentiment_hero_title", "Rate. Reflect. Repeat.")}</h1>
            <p className="text-neutral-500 mt-3 max-w-2xl" data-testid="sentiment-hero-subtitle">{T("sentiment_hero_subtitle", "Pakistan's living scorecard for the people, brands, and personalities that shape our culture. Every star is one verified voter. Sign in to rate, anyone can read.")}</p>
          </div>
          <div className="mt-4 md:mt-0 md:w-72 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="sentiment-search"
              placeholder="Search names..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-full pl-9 border border-neutral-200"
            />
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 flex-wrap mb-8" data-testid="sentiment-categories">
          <CatChip active={active === ""} onClick={() => setActive("")} testid="cat-all">All</CatChip>
          {categories.map((c) => (
            <CatChip key={c.slug} active={active === c.slug} onClick={() => setActive(c.slug)} testid={`cat-${c.slug}`}>{c.name}</CatChip>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="sentiment-grid">
          {items.map((it) => (
            <Link
              key={it.id}
              to={`${base}/${it.slug}`}
              data-testid={`sentiment-card-${it.slug}`}
              className="group border border-neutral-200 rounded-2xl bg-white p-5 shadow-sm hover:shadow-md hover:border-mango/40 transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-neutral-100 overflow-hidden flex-shrink-0">
                  {it.photo_url ? <img src={it.photo_url} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-2xl text-neutral-300">{it.name[0]}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold tracking-tight truncate text-neutral-900">{it.name}</div>
                  <div className="overline text-neutral-400 mt-1">{it.category_slug}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Stars value={it.avg_rating} size={16} />
                    <span className="text-sm font-bold text-neutral-900">{it.avg_rating ?? "—"}</span>
                    {showLabels && it.sentiment_label && <SentimentLabelChip label={it.sentiment_label} />}
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">Based on {it.total_votes} verified oodel member{it.total_votes === 1 ? "" : "s"}</div>
                </div>
              </div>
            </Link>
          ))}
          {!loading && items.length === 0 && <div className="col-span-full text-center py-12 text-neutral-500">No items yet in this category.</div>}
        </div>

        <SentimentDisclaimer />
      </div>
    </div>
  );
}

function SentimentDisclaimer() {
  const c = useSiteContent();
  const text = c.sentiment_disclaimer !== undefined
    ? c.sentiment_disclaimer
    : "oodel Sentiment reflects the current perceptions of verified oodel respondents. It does not represent objective fact, service quality or an endorsement by oodel. Sentiment naturally changes over time as public perceptions evolve.";
  return (
    <p className="mt-12 pt-6 border-t border-neutral-100 text-xs text-neutral-400 text-center max-w-3xl mx-auto" data-testid="sentiment-disclaimer">
      {text}
    </p>
  );
}

// Small pill mapping a sentiment_label ("Very Positive" -> "Very Negative")
// to a brand-consistent color — green for positive, gray for neutral, red
// for negative. Shared by both the list-grid cards and the detail page.
export function SentimentLabelChip({ label }) {
  if (!label) return null;
  const cls = label.includes("Positive")
    ? "bg-mango/10 text-mango"
    : label === "Neutral"
      ? "bg-neutral-100 text-neutral-500"
      : "bg-red-50 text-red-600";
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${cls}`} data-testid="sentiment-label-chip">{label}</span>;
}

function CatChip({ children, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`px-4 py-2 text-sm rounded-full border transition-colors ${active ? "border-mango bg-mango text-white font-bold" : "border-neutral-200 text-neutral-600 hover:border-mango/50"}`}>{children}</button>
  );
}

// ============================================================================
// /sentiment/:slug — detail
// ============================================================================
export function SentimentDetail() {
  const { slug } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const c = useSiteContent();
  const T = (k, fallback) => (c[k] !== undefined ? c[k] : fallback);
  const showLabels = c.sentiment_public_labels_enabled !== "false";
  const inPanel = loc.pathname.startsWith("/panelist");
  const base = inPanel ? "/panelist/sentiment" : "/sentiment";
  const detailPath = `${base}/${slug}`;
  const { user } = useAuth();
  const [data, setData] = useState(() => api.getCached(`/sentiment/items/${slug}`));
  const [voting, setVoting] = useState(false);
  const [hover, setHover] = useState(0);
  // ITER 110 — same profile-completion gate as taking a survey. A patron
  // with an incomplete profile can still READ every rating/breakdown on
  // this page (matches "public content stays viewable" everywhere else),
  // they just can't SUBMIT a rating until they've filled in their profile.
  const prof = user?.profile || {};
  const profileComplete = !!prof.profile_complete && (prof.sectors_of_interest || []).length >= 3;

  const load = () => api.get(`/sentiment/items/${slug}`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (stars) => {
    if (!user) { nav(`/login`, { state: { from: detailPath } }); return; }
    if (!profileComplete) { toast.error("Complete your profile first to rate."); return; }
    setVoting(true);
    try {
      await api.post(`/sentiment/items/${slug}/vote`, { stars });
      toast.success("Thanks — your rating is in!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit vote");
    } finally {
      setVoting(false);
    }
  };

  if (data === null) return <div className="min-h-screen bg-white themable-public"><OuterNav /><div className="p-12 text-center">Loading…</div></div>;

  const cooldownDays = data.cooldown_days || 180;
  const myVote = data.my_vote;
  const lastVotedISO = myVote?.updated_at || myVote?.first_voted_at;
  const lastVotedDate = lastVotedISO ? new Date(lastVotedISO) : null;
  const eligibleAt = lastVotedDate ? new Date(lastVotedDate.getTime() + cooldownDays * 86400000) : null;
  const canVoteNow = !lastVotedDate || (Date.now() >= eligibleAt.getTime());

  // Distribution as percentages (industry standard, like Trustpilot/Glassdoor)
  const histTotal = Object.values(data.histogram || {}).reduce((a, b) => a + (b || 0), 0);
  const pct = (n) => (histTotal ? Math.round(((n || 0) / histTotal) * 1000) / 10 : 0);

  return (
    <div className="min-h-screen bg-white themable-public">
      <OuterNav />
      <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <Link to={base} className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-mango mb-6"><ArrowLeft size={14} /> All Public Sentiment</Link>

        {/* Hero card */}
        <div className="border border-neutral-200 rounded-2xl bg-white p-5 md:p-8 mb-6 shadow-sm">
          <div className="grid md:grid-cols-[140px_1fr_auto] gap-6">
            <div className="w-28 h-28 md:w-[140px] md:h-[140px] rounded-2xl bg-neutral-100 overflow-hidden">
              {data.photo_url ? <img src={data.photo_url} alt={data.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-5xl text-neutral-300">{data.name[0]}</div>}
            </div>
            <div className="min-w-0">
              <div className="overline text-neutral-400">{data.category_slug}</div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900" data-testid="sentiment-detail-name">{data.name}</h1>
              {data.description && <p className="text-neutral-500 mt-2">{data.description}</p>}
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-wide text-neutral-400 mb-1.5" data-testid="sentiment-current-label">Current Sentiment</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Stars value={data.avg_rating} size={24} />
                  <span className="text-3xl font-bold text-neutral-900" data-testid="sentiment-aggregate">{data.avg_rating ?? "—"}</span>
                  <span className="text-neutral-400 text-sm">/ 5</span>
                  {showLabels && data.sentiment_label && <SentimentLabelChip label={data.sentiment_label} />}
                </div>
                <div className="text-xs text-neutral-400 mt-1.5">Based on <span data-testid="sentiment-total-ratings">{data.total_votes}</span> verified oodel member{data.total_votes === 1 ? "" : "s"}</div>
              </div>
            </div>
            <div className="flex md:flex-col items-start gap-2">
              {user && <WatchlistButton slug={slug} />}
            </div>
          </div>
        </div>

        {/* Vote panel */}
        <div className="border border-neutral-200 rounded-2xl bg-white p-5 md:p-8 mb-6 shadow-sm" data-testid="sentiment-vote-panel">
          <div className="overline text-mango">{myVote ? "YOUR CURRENT RATING" : "YOUR RATING"}</div>
          <h2 className="text-2xl font-bold tracking-tight mb-3 text-neutral-900">
            {!user ? "Sign in to rate" : !profileComplete ? "Complete your profile to rate" : (canVoteNow ? (myVote ? "Update your rating" : T("sentiment_vote_prompt", "How would you rate them today?")) : "Locked until your re-rate window opens")}
          </h2>
          {user && profileComplete && (
            <>
              <div className="flex items-center gap-2 mb-2" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    data-testid={`star-${s}`}
                    disabled={voting || !canVoteNow}
                    onMouseEnter={() => setHover(s)}
                    onClick={() => submit(s)}
                    className={`text-4xl ${(hover ? hover >= s : (myVote?.stars || 0) >= s) ? "text-mango" : "text-neutral-300"} hover:scale-110 transition disabled:cursor-not-allowed disabled:opacity-70`}
                    aria-label={`${s} star${s === 1 ? "" : "s"}`}
                  >
                    <Star weight="fill" />
                  </button>
                ))}
              </div>
              {/* Live label of the star currently hovered/selected, so a
                  patron always knows exactly what they're about to submit. */}
              <p className="text-xs font-bold text-mango mb-3 h-4" data-testid="sentiment-hover-label">
                {(hover || myVote?.stars) ? SENTIMENT_SCALE.find((s) => s.stars === (hover || myVote?.stars))?.label : ""}
              </p>
            </>
          )}
          {!user ? (
            <Button onClick={() => nav("/login", { state: { from: detailPath } })} className="rounded-full bg-mango hover:bg-mango/90" data-testid="sentiment-login-cta">
              <Lock size={14} className="mr-2" /> Sign in to rate
            </Button>
          ) : !profileComplete ? (
            <div data-testid="sentiment-profile-gate">
              <p className="text-sm text-neutral-500 mb-3">Ratings need a completed profile so we know your demographic slice is real — takes about 2 minutes.</p>
              <Link to="/panelist/profile" state={{ from: detailPath }}>
                <Button className="rounded-full bg-mango hover:bg-mango/90" data-testid="sentiment-complete-profile-cta">
                  <Lock size={14} className="mr-2" /> Complete your profile to rate
                </Button>
              </Link>
            </div>
          ) : myVote ? (
            canVoteNow ? (
              <p className="text-sm text-neutral-500">You last rated <b>{myVote.stars}★</b> on {formatDate(myVote.updated_at || myVote.first_voted_at)}. Re-rate to see how your view has shifted.</p>
            ) : (
              <p className="text-sm text-neutral-500">You last rated <b>{myVote.stars}★</b> on {formatDate(lastVotedISO)}. You can re-rate from <b>{formatDate(eligibleAt)}</b> ({cooldownDays}-day cooldown).</p>
            )
          ) : (
            <p className="text-sm text-neutral-500" data-testid="sentiment-first-vote-note">{T("sentiment_first_vote_note", "Your first rating is permanent for {days} days — then you can update it.").replace("{days}", cooldownDays)}</p>
          )}

          {/* Scale legend — always visible so a reader/voter knows exactly
              what each star means before (or after) rating. */}
          <div className="mt-5 pt-4 border-t border-neutral-100 flex flex-wrap gap-x-4 gap-y-1" data-testid="sentiment-scale-legend">
            {SENTIMENT_SCALE.map((s) => (
              <span key={s.stars} className="text-xs text-neutral-400">
                <b className="text-neutral-600">{s.stars}★</b> = {s.label}
              </span>
            ))}
          </div>
        </div>

        {/* Rating distribution — shown to everyone */}
        <div className="border border-neutral-200 rounded-2xl bg-white p-5 md:p-6 mb-6 shadow-sm" data-testid="sentiment-distribution">
          <div className="overline mb-3 text-mango">RATING DISTRIBUTION</div>
          {[5, 4, 3, 2, 1].map((s) => {
            const n = data.histogram?.[s] || 0;
            const p = pct(n);
            return (
              <div key={s} className="flex items-center gap-3 mb-2" data-testid={`hist-${s}`}>
                <div className="text-sm w-6 text-neutral-600">{s}★</div>
                <div className="flex-1 bg-neutral-100 rounded-full h-4 relative overflow-hidden">
                  <div className="bg-mango h-4 rounded-full" style={{ width: `${p}%` }} />
                </div>
                <div className="text-sm w-16 text-right text-neutral-500">{p}%</div>
              </div>
            );
          })}
          <div className="text-xs text-neutral-400 mt-2">Based on {histTotal} rating{histTotal === 1 ? "" : "s"}</div>
        </div>

        {/* 12-month trend chart — ONLY when admin toggled show_public_trend AND
            there's enough monthly data. Silent hide otherwise (no "not enough
            data" message per product spec). */}
        {data.show_public_trend && (data.trend_12m || []).filter((m) => m.n > 0).length >= 2 && (
          <div className="border border-neutral-200 rounded-2xl bg-white p-5 md:p-6 mb-6 shadow-sm" data-testid="sentiment-trend-12m">
            <div className="overline mb-3 flex items-center gap-2 text-mango"><ChartLineUp size={14} /> 12-MONTH TREND</div>
            <MonthlySparkline points={data.trend_12m || []} />
            <div className="text-xs text-neutral-400 mt-2">How {data.name}'s rating has shifted over the last 12 months. Each point is the monthly average.</div>
          </div>
        )}

        {/* "How your view has shifted" — patron-only, only when they have history */}
        {user && data.my_history?.length > 0 && (
          <div className="border border-neutral-200 rounded-2xl bg-white p-5 md:p-6 shadow-sm" data-testid="my-history-chart">
            <div className="overline mb-3 flex items-center gap-2 text-mango"><ChartLineUp size={14} /> HOW YOUR VIEW HAS SHIFTED</div>
            <SparkLine points={data.my_history.map((h) => ({ x: h.voted_at, y: h.stars }))} />
            <p className="text-xs text-neutral-400 mt-2">Every {Math.round(cooldownDays / 30)} months you'll get a nudge to re-rate. Your line traces how your view evolves.</p>
          </div>
        )}

        <SentimentDisclaimer />
      </div>
    </div>
  );
}

// Larger 12-month line chart for the dedicated card.
function MonthlySparkline({ points }) {
  if (!points || points.length === 0) return <div className="text-sm text-neutral-500">No rating activity yet.</div>;
  const W = 600, H = 140, P = 18;
  const have = points.filter((p) => p.avg != null);
  if (have.length < 1) return <div className="text-sm text-neutral-500">No rating activity yet.</div>;
  const xs = (i) => P + (i / Math.max(1, points.length - 1)) * (W - 2 * P);
  // Fix y-axis to 1..5 (rating scale)
  const ys = (v) => v == null ? null : H - P - ((v - 1) / 4) * (H - 2 * P);
  let d = "";
  let started = false;
  points.forEach((p, i) => {
    const y = ys(p.avg);
    if (y == null) return;
    d += `${started ? "L" : "M"}${xs(i).toFixed(1)} ${y.toFixed(1)} `;
    started = true;
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none">
      {[1, 2, 3, 4, 5].map((y) => (
        <line key={y} x1={P} x2={W - P} y1={ys(y)} y2={ys(y)} stroke="#eee" />
      ))}
      <path d={d} fill="none" stroke="#16A34A" strokeWidth={2.5} />
      {points.map((p, i) => {
        const y = ys(p.avg);
        if (y == null) return null;
        return <circle key={i} cx={xs(i)} cy={y} r={3} fill="#16A34A" />;
      })}
      {/* X-axis labels: first, middle, last month */}
      {[0, Math.floor(points.length / 2), points.length - 1].map((i) => (
        <text key={i} x={xs(i)} y={H - 2} fontSize="9" fill="#999" textAnchor="middle">{points[i]?.month?.slice(2)}</text>
      ))}
    </svg>
  );
}

function WatchlistButton({ slug }) {
  const [watched, setWatched] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/panelist/watchlist/check/${slug}`).then((r) => setWatched(!!r.data?.watched)).catch(() => {});
  }, [slug]);
  const toggle = async () => {
    setBusy(true);
    try {
      if (watched) {
        await api.delete(`/panelist/watchlist/${slug}`);
        setWatched(false);
        toast.success("Removed from watchlist");
      } else {
        await api.post(`/panelist/watchlist/${slug}`);
        setWatched(true);
        toast.success("Added to watchlist");
      }
    } catch {
      toast.error("Could not update watchlist");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid="watchlist-toggle"
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-bold transition-colors ${watched ? "border-mango bg-mango/10 text-neutral-900" : "border-neutral-200 bg-white hover:border-mango/40"}`}
      aria-pressed={watched}
      title={watched ? "Remove from watchlist" : "Add to watchlist"}
    >
      <Star weight={watched ? "fill" : "regular"} size={16} className={watched ? "text-mango" : "text-neutral-500"} />
      {watched ? "Watching" : "Watch"}
    </button>
  );
}

function Stars({ value, size = 16 }) {
  if (value == null) return <span className="text-neutral-300"><Star size={size} weight="fill" /></span>;
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <span className="inline-flex items-center text-mango">
      {[...Array(5)].map((_, i) => {
        if (i < full) return <Star key={i} size={size} weight="fill" />;
        if (i === full && half) return <StarHalf key={i} size={size} weight="fill" />;
        return <Star key={i} size={size} className="text-neutral-300" weight="fill" />;
      })}
    </span>
  );
}

function SparkLine({ points }) {
  if (!points || points.length === 0) return null;
  const W = 600, H = 100, P = 12;
  const minX = new Date(points[0].x).getTime();
  const maxX = new Date(points[points.length - 1].x).getTime();
  const span = Math.max(1, maxX - minX);
  const coord = (p) => ({
    cx: P + ((new Date(p.x).getTime() - minX) / span) * (W - 2 * P),
    cy: H - P - ((p.y - 1) / 4) * (H - 2 * P),
  });
  const d = points.map((p, i) => {
    const c = coord(p);
    return `${i === 0 ? "M" : "L"}${c.cx.toFixed(1)} ${c.cy.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none">
      {[1, 2, 3, 4, 5].map((y) => (
        <line key={y} x1={P} x2={W - P} y1={H - P - ((y - 1) / 4) * (H - 2 * P)} y2={H - P - ((y - 1) / 4) * (H - 2 * P)} stroke="#eee" />
      ))}
      <path d={d} fill="none" stroke="#16A34A" strokeWidth={2.5} />
      {points.map((p, i) => {
        const c = coord(p);
        return <circle key={i} cx={c.cx} cy={c.cy} r={3.5} fill="#16A34A" />;
      })}
    </svg>
  );
}
